import { createFileRoute } from '@tanstack/react-router'
import * as v from 'valibot'
import { useSessionUser } from '@/hooks/use-session'
import { Suspense } from 'react'
import { cn, formatDateLocale, formatNumber } from '@/lib/utils'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import {
  CalendarIcon,
  CheckIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  ReceiptIcon,
  SparklesIcon,
} from '@/routes/_app/-components/settings/settings-icons'
import { Button } from '@/components/ui/button'
import { useAppForm } from '@/lib/use-app-form'
import { trpc } from '@/main'
import { useTranslation } from '@/lib/locales'
import { Spinner } from '@/components/ui/spinner'
import { SETTINGS_FIELDS, SETTINGS_STACK, SettingsSection } from './-components/settings/settings-section'
import { sectionTone } from './-components/shared/section-heading'
import { BillingPending, BillingPlanSkeleton } from './-components/settings/account-pending'

// Constant locale + currency, so build the formatter once instead of on every render.
const eurFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' })

export const Route = createFileRoute('/_app/account/billing')({
  context: () => ({ billingQueryOptions: trpc.billing.get.queryOptions() }),
  loader: ({ context }) => {
    context.queryClient.query(context.billingQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: BillingPending,
})

const getStatusColor = (status: string | undefined, canceled: boolean | undefined) => {
  if (canceled || status === 'canceled') return 'bg-destructive text-destructive-foreground'
  if (status === 'active') return 'bg-emerald-500 text-white'
  return 'bg-amber-500 text-white'
}

function RouteComponent() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())

  // Defined inside the component (like every other form route) so the zod import lands in this
  // route's lazy component chunk, not the eager route-definition bundle. A module-level zod schema
  // here was the sole thing dragging zod's runtime (~72 kB) into the main entry chunk.
  const form = useAppForm({
    validators: {
      // Every field is required — they drive the "*" marker and block save when empty — bar the postal code.
      onChange: v.object({
        billingEmail: v.pipe(v.string(), v.email()),
        billingName: v.pipe(v.string(), v.minLength(1)),
        nif: v.pipe(v.string(), v.minLength(1)),
        billingCountry: v.pipe(v.string(), v.minLength(1)),
        billingProvince: v.pipe(v.string(), v.minLength(1)),
        billingCity: v.pipe(v.string(), v.minLength(1)),
        address: v.pipe(v.string(), v.minLength(1)),
        // Postal code is the one optional field: a plain string with no `.min(1)`, so an empty value
        // passes and never trips the "Required" / aria-invalid state (defaultValues coerce null→'').
        cp: v.string(),
      }),
    },
    defaultValues: {
      billingEmail: user?.billingEmail ?? '',
      billingName: user?.billingName ?? '',
      nif: user?.nif ?? '',
      billingCountry: user?.billingCountry ?? '',
      billingProvince: user?.billingProvince ?? '',
      billingCity: user?.billingCity ?? '',
      address: user?.address ?? '',
      cp: user?.cp ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
    },
  })

  return (
    <div className={SETTINGS_STACK}>
      <Suspense fallback={<BillingPlanSkeleton icons={{ plan: CreditCardIcon, plans: SparklesIcon }} />}>
        <BillingPlan />
      </Suspense>

      <SettingsSection
        icon={ReceiptIcon}
        tone={sectionTone('amber')}
        title={t`Billing information`}
        description={t`Your billing address and tax information`}
        actions={
          <form.AppForm>
            <form.SaveDiscardButtons />
          </form.AppForm>
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            form.handleSubmit()
          }}
          className={SETTINGS_FIELDS}
        >
          <form.AppField
            name="billingEmail"
            children={(field) => <field.TextField label={t`Billing email`} type="email" orientation="horizontal" />}
          />
          <form.AppField
            name="billingName"
            children={(field) => <field.TextField label={t`Name or company name`} orientation="horizontal" />}
          />
          <form.AppField
            name="nif"
            children={(field) => <field.TextField label={t`NIF, VAT, TAX ID...`} orientation="horizontal" />}
          />
          <form.AppField
            name="billingCountry"
            listeners={{ onChange: () => form.setFieldValue('billingProvince', '') }}
            children={(field) => <field.NativeCountryField label={t`Country`} orientation="horizontal" />}
          />
          <form.Subscribe selector={(state) => state.values.billingCountry}>
            {(country) => (
              <form.AppField
                name="billingProvince"
                children={(field) => (
                  <field.NativeProvinceField label={t`State / Province`} orientation="horizontal" country={country} />
                )}
              />
            )}
          </form.Subscribe>
          <form.AppField
            name="billingCity"
            children={(field) => (
              <field.TextField label={t`City`} orientation="horizontal" autoComplete="address-level2" />
            )}
          />
          <form.AppField
            name="address"
            children={(field) => (
              <field.TextField label={t`Address`} orientation="horizontal" autoComplete="street-address" />
            )}
          />
          <form.AppField
            name="cp"
            children={(field) => (
              <field.TextField label={t`Postal code`} orientation="horizontal" autoComplete="postal-code" />
            )}
          />
        </form>
      </SettingsSection>
    </div>
  )
}

function BillingPlan() {
  const { t, i18n } = useLingui()
  const { language } = useTranslation()
  const { data: customer } = useSuspenseQuery(Route.useRouteContext().billingQueryOptions)
  const user = useSessionUser()
  const { mutateAsync: retrieveBillingIntent, isPending } = useMutation(trpc.billing.retrieveIntent.mutationOptions())
  const { mutateAsync: retrieveBillingPortal, isPending: isPendingPortal } = useMutation(
    trpc.billing.retrievePortal.mutationOptions(),
  )

  // Builds the plan's feature list from its structured fields (account type + monthly limits; -1 means
  // unlimited), so the backend stays language-agnostic instead of returning a localized bullet string.
  const planFeatures = (plan: (typeof customer.plans)[number]): string[] => {
    const lines: string[] = []
    if (user?.type === 'applicant' && plan.accountType) {
      lines.push(plan.accountType === 'advanced' ? t`Advanced report of your profile` : t`Basic report of your profile`)
    }
    const products = plan.limitProduct == null ? '' : formatNumber(i18n.locale, plan.limitProduct, 0)
    lines.push(
      plan.limitProduct === -1
        ? t`Unlimited team and professional position reports`
        : t`${products} reports of positions, professionals and teams by month`,
    )
    const comparisons = plan.limitComparison == null ? '' : formatNumber(i18n.locale, plan.limitComparison, 0)
    lines.push(
      plan.limitComparison === -1
        ? t`Free and unlimited comparison reports`
        : t`${comparisons} monthly comparison reports`,
    )
    const markets = plan.limitMarket == null ? '' : formatNumber(i18n.locale, plan.limitMarket, 0)
    lines.push(
      plan.limitMarket === -1
        ? t`Free and unlimited market profile reports`
        : t`${markets} monthly market profile reports`,
    )
    return lines
  }

  const planName = (name: string | null) => {
    return name?.includes('free')
      ? t`Free`
      : name?.includes('standard')
        ? t`Company`
        : name?.includes('plus')
          ? t`Integration`
          : t`Customized`
  }

  function getBillingStatusName(
    status: 'trialing' | 'active' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'canceled' | 'unpaid',
  ): string {
    switch (status) {
      case 'trialing':
        return t`proof`
      case 'active':
        return t`active`
      case 'incomplete':
      case 'incomplete_expired':
        return t`incomplete`
      case 'past_due':
        return t`defeated`
      case 'canceled':
        return t`cancelled`
      case 'unpaid':
        return t`not payed`
    }
  }

  return (
    <>
      <SettingsSection
        icon={CreditCardIcon}
        tone={sectionTone('amber')}
        title={t`Current plan`}
        description={t`Your subscription details and usage`}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-2xl font-semibold tracking-tight">{planName(customer.subscription.plan)}</h4>
                  {customer.subscription.plan !== 'free' && customer.subscription.status && (
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize',
                        getStatusColor(customer.subscription.status, customer.subscription.planCanceled ?? undefined),
                      )}
                    >
                      {getBillingStatusName(
                        (customer.subscription.planCanceled ? 'canceled' : customer.subscription.status) as
                          | 'trialing'
                          | 'active'
                          | 'incomplete'
                          | 'incomplete_expired'
                          | 'past_due'
                          | 'canceled'
                          | 'unpaid',
                      )}
                    </span>
                  )}
                </div>
                {customer.subscription.plan !== 'free' && customer.subscription.price && (
                  <p className="text-muted-foreground text-sm">
                    {eurFormatter.format(customer.subscription.price / 100)} / {t`Month`}
                  </p>
                )}
              </div>
            </div>
            {/* Stripe's portal only has something to show (invoices, payment methods, the subscription) once
                Stripe knows the account; a free account that never paid gets the plan cards instead. */}
            {customer.hasBillingAccount && (
              <Button
                onClick={() =>
                  retrieveBillingPortal().then(({ customerPortalUrl }) => {
                    window.location.href = customerPortalUrl
                  })
                }
                disabled={isPendingPortal}
                variant="secondary"
              >
                {isPendingPortal && <Spinner className="size-4" />}
                {customer.subscription.plan === 'free' ? t`Invoices and payment methods` : t`Manage subscription`}
                <ExternalLinkIcon className="size-4" />
              </Button>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <UsageStat
              label={t`Reports consumed`}
              current={customer.subscription.product.count}
              limit={customer.subscription.product.limit}
            />
            <UsageStat
              label={t`Consumed comparisons`}
              current={customer.subscription.comparison.count}
              limit={customer.subscription.comparison.limit}
            />
            <UsageStat
              label={t`Market profiles`}
              current={customer.subscription.market.count}
              limit={customer.subscription.market.limit}
            />
          </div>

          <div className="bg-muted/50 flex flex-wrap gap-6 rounded-lg p-4 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="text-muted-foreground size-4" />
              <span className="text-muted-foreground">{t`Beginning of the plan`}:</span>
              <span className="font-medium">{formatDateLocale(customer.subscription.planStart, language)}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="text-muted-foreground size-4" />
              <span className="text-muted-foreground">{t`End of the plan`}:</span>
              <span className="font-medium">{formatDateLocale(customer.subscription.planEnd, language)}</span>
            </div>
          </div>
        </div>
      </SettingsSection>

      {customer.subscription.plan === 'free' && customer.plans.length > 0 && (
        <SettingsSection
          icon={SparklesIcon}
          tone={sectionTone('amber')}
          title={t`Plans available`}
          description={t`Upgrade your plan to unlock more features`}
        >
          {/* auto-fit: however many plans there are, the cards share the section's full width. */}
          <div className="grid gap-4 sm:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]">
            {customer.plans.map((plan) => (
              <div
                key={plan.id}
                role="button"
                tabIndex={0}
                onClick={() =>
                  retrieveBillingIntent({ productId: plan.id, productType: 'subscription' }).then(({ sessionUrl }) => {
                    window.location.href = sessionUrl
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    retrieveBillingIntent({ productId: plan.id, productType: 'subscription' }).then(
                      ({ sessionUrl }) => {
                        window.location.href = sessionUrl
                      },
                    )
                  }
                }}
                className={cn(
                  // The landing's pricing card: a neutral surface, the price as the headline figure, lifted on hover.
                  'group relative flex cursor-pointer flex-col gap-4 rounded-2xl border bg-background p-5 text-left transition-[border-color,box-shadow] hover:border-foreground/25 hover:shadow-[0_30px_70px_-44px_rgb(24_54_48/0.42)] dark:hover:shadow-none',
                  isPending && 'pointer-events-none opacity-70',
                )}
              >
                <div className="flex flex-col gap-1">
                  <h4 className="font-semibold">{planName(plan.name)}</h4>
                  <p className="text-3xl font-semibold tracking-tight tabular-nums">
                    {eurFormatter.format((plan.price ?? 0) / 100)}
                    <span className="text-muted-foreground text-sm font-normal"> / {t`Month`}</span>
                  </p>
                </div>
                <ul className="flex flex-col gap-2 border-t pt-4 text-sm">
                  {planFeatures(plan).map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <CheckIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-muted-foreground group-hover:text-foreground mt-auto flex items-center gap-2 text-sm font-medium">
                  <span>{t`Select plan`}</span>
                  <CheckIcon className="size-4 opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </div>
            ))}
          </div>
        </SettingsSection>
      )}
    </>
  )
}

function UsageStat(props: { label: string; current: number; limit: number }) {
  const { i18n } = useLingui()
  const percentage = props.limit === -1 ? 0 : props.limit > 0 ? Math.min((props.current / props.limit) * 100, 100) : 0
  const limitDisplay = props.limit === -1 ? '∞' : formatNumber(i18n.locale, props.limit, 0)

  return (
    <div className="bg-background flex flex-col gap-2 rounded-xl border p-4">
      <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">{props.label}</span>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-semibold tracking-tight tabular-nums">
          {formatNumber(i18n.locale, props.current, 0)}
        </span>
        <span className="text-muted-foreground text-sm">/ {limitDisplay}</span>
      </div>
      {props.limit !== -1 && props.limit > 0 && (
        <div className="bg-muted h-1 overflow-hidden rounded-full">
          <div
            className={cn(
              'h-full rounded-full transition-[width]',
              percentage >= 90 ? 'bg-destructive' : percentage >= 70 ? 'bg-amber-500' : 'bg-foreground/60',
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  )
}
